'use client';

import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from './UpgradeScreen.translations';
import { loadSounds, playSfx } from '../../lib/audioEngine';
import { FaExclamationTriangle } from '../../components/icons';

interface UpgradeDialProps {
  probability: number | null;
  coefficient?: number | null;
  isSpinning: boolean;
  spinResult: 'win' | 'lose' | null;
  animationsEnabled?: boolean;
  size?: number;
  emptyHint?: string;
  onHintRetry?: () => void;
}

const VB = 220;
const C = VB / 2;
const TICK_R_OUTER = 100;
const TICK_R_INNER = 90;
const ARC_R = 80;
const ARC_C = 2 * Math.PI * ARC_R;
const TICK_COUNT = 60;
export const DIAL_SPIN_MS = 6000;
export const DIAL_FAST_SPIN_MS = 700;
export const DIAL_FLASH_MS = 1300;
export const DIAL_FAST_FLASH_MS = 450;
export const DIAL_WIN_MS = 700;
export const DIAL_FAST_WIN_MS = 250;
const WIN_COLOR = '#FFD60A';
const ODDS_TWEEN_MS = 420;
const SPIN_EASE = 'cubic-bezier(0.35, 0, 0.45, 1)';
const FAST_EASE = 'cubic-bezier(0.2, 0.8, 0.2, 1)';

const TICK_DEG = 30;
const makeCubicBezier = (x1: number, y1: number, x2: number, y2: number): ((x: number) => number) => {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sampleX = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number): number => ((ay * t + by) * t + cy) * t;
  return (x: number): number => {
    let t = Math.max(0, Math.min(1, x));
    for (let i = 0; i < 8; i++) {
      const dx = (3 * ax * t + 2 * bx) * t + cx;
      if (Math.abs(dx) < 1e-6) break;
      t -= (sampleX(t) - x) / dx;
    }
    return sampleY(Math.max(0, Math.min(1, t)));
  };
};
const easeSpin = makeCubicBezier(0.35, 0, 0.45, 1);
const easeFast = makeCubicBezier(0.2, 0.8, 0.2, 1);

function chanceColor(probability: number): string {
  const stops = [
    [255, 69, 58],
    [255, 214, 0],
    [124, 255, 107],
  ];
  const norm = Math.min(Math.max(probability / 0.5, 0), 1);
  const seg = norm * (stops.length - 1);
  const i = Math.min(Math.floor(seg), stops.length - 2);
  const f = seg - i;
  const c = stops[i].map((v, k) => Math.round(v + (stops[i + 1][k] - v) * f));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

function computeLanding(current: number, probability: number, win: boolean): number {
  const half = Math.min(Math.max(probability * 180, 6), 174);
  const margin = 6;
  let target: number;
  if (win) {
    const reach = Math.max(half - margin, 1);
    target = (Math.random() * 2 * reach - reach + 360) % 360;
  } else {
    const lo = half + margin;
    const hi = Math.max(360 - half - margin, lo + 1);
    target = lo + Math.random() * (hi - lo);
  }
  const turns = 3 + Math.floor(Math.random() * 2);
  const adj = (target - (current % 360) + 360) % 360;
  return current + turns * 360 + adj;
}

const NEUTRAL_COLOR = 'rgba(255, 255, 255, 0.45)';

export const UpgradeDial: React.FC<UpgradeDialProps> = ({ probability, coefficient = null, isSpinning, spinResult, animationsEnabled = true, size = 150, emptyHint, onHintRetry }) => {
  const { t } = useLocalTranslation(translations);
  const isKnown = probability !== null;
  const pct = probability !== null ? Math.round(probability * 1000) / 10 : null;
  const label = probability !== null ? (coefficient !== null ? `×${coefficient.toFixed(2)}` : '') : (emptyHint ?? '');
  const color = probability !== null ? chanceColor(probability) : NEUTRAL_COLOR;
  const arcLen = probability !== null ? probability * ARC_C : 0;
  const arcRotateDeg = probability !== null ? 90 - probability * 180 : 90;
  const spinMs = animationsEnabled ? DIAL_SPIN_MS : DIAL_FAST_SPIN_MS;
  const spinEase = animationsEnabled ? SPIN_EASE : FAST_EASE;
  const flashMs = animationsEnabled ? DIAL_FLASH_MS : DIAL_FAST_FLASH_MS;

  const [rotation, setRotation] = useState(0);
  const [resolved, setResolved] = useState(false);
  const spun = useRef(false);
  const prevSpinning = useRef(false);
  const rotationRef = useRef(0);
  const tickRafRef = useRef<number | null>(null);
  const arcRef = useRef<SVGCircleElement | null>(null);
  const pctRef = useRef<HTMLSpanElement | null>(null);
  const labelRef = useRef<HTMLSpanElement | null>(null);
  const shownRef = useRef<{ probability: number; coefficient: number } | null>(
    probability !== null ? { probability, coefficient: coefficient ?? 0 } : null,
  );
  const tweenRafRef = useRef<number | null>(null);
  const paintedRef = useRef<{ probability: number; coefficient: number } | null>(null);

  const paintOdds = (p: number, coef: number): void => {
    const paintColor = chanceColor(p);
    const len = p * ARC_C;
    if (arcRef.current) {
      arcRef.current.setAttribute('stroke-dasharray', `${len} ${ARC_C - len}`);
      arcRef.current.setAttribute('transform', `rotate(${90 - p * 180} ${C} ${C})`);
      arcRef.current.setAttribute('stroke', paintColor);
      arcRef.current.style.filter = `drop-shadow(0 0 10px ${paintColor}) drop-shadow(0 0 4px ${paintColor}aa)`;
    }
    if (pctRef.current) {
      pctRef.current.textContent = `${(p * 100).toFixed(2)}%`;
      pctRef.current.style.color = paintColor;
    }
    if (labelRef.current && coef > 0) labelRef.current.textContent = `×${coef.toFixed(2)}`;
    paintedRef.current = { probability: p, coefficient: coef };
  };

  // WHY: догоняем новое значение рисованием в DOM — React-ререндер на каждый кадр перетряхивал бы весь дайл
  useLayoutEffect(() => {
    if (tweenRafRef.current) {
      cancelAnimationFrame(tweenRafRef.current);
      tweenRafRef.current = null;
    }

    const next = probability === null ? null : { probability, coefficient: coefficient ?? 0 };
    const from = paintedRef.current ?? shownRef.current;
    shownRef.current = next;
    if (!next) paintedRef.current = null;

    if (!next || !from || !animationsEnabled || isSpinning || spinResult) return;
    if (Math.abs(next.probability - from.probability) < 0.0002) return;

    paintOdds(from.probability, from.coefficient || next.coefficient);

    const t0 = performance.now();
    const step = (): void => {
      const t = Math.min(1, (performance.now() - t0) / ODDS_TWEEN_MS);
      if (t >= 1) {
        paintOdds(next.probability, next.coefficient);
        tweenRafRef.current = null;
        return;
      }
      const e = easeFast(t);
      paintOdds(
        from.probability + (next.probability - from.probability) * e,
        (from.coefficient || next.coefficient) + (next.coefficient - (from.coefficient || next.coefficient)) * e,
      );
      tweenRafRef.current = t < 1 ? requestAnimationFrame(step) : null;
    };
    tweenRafRef.current = requestAnimationFrame(step);

    return () => {
      if (tweenRafRef.current) {
        cancelAnimationFrame(tweenRafRef.current);
        tweenRafRef.current = null;
        paintOdds(next.probability, next.coefficient);
      }
    };
  }, [probability, coefficient, animationsEnabled, isSpinning, spinResult]);

  useEffect(() => {
    void loadSounds(['click_wheel', 'upgrade_fail', 'jackpot', 'item_drop']).catch(() => undefined);
    return () => { if (tickRafRef.current) cancelAnimationFrame(tickRafRef.current); };
  }, []);

  useEffect(() => {
    if (isSpinning && !prevSpinning.current) {
      setResolved(false);
      spun.current = false;
    }
    prevSpinning.current = isSpinning;

    if (isSpinning && spinResult && !spun.current && probability !== null) {
      spun.current = true;
      const startRot = rotationRef.current;
      const finalRot = computeLanding(startRot, probability, spinResult === 'win');
      rotationRef.current = finalRot;
      setRotation(finalRot);

      const ease = animationsEnabled ? easeSpin : easeFast;
      const dist = Math.abs(finalRot - startRot);
      const dir = Math.sign(finalRot - startRot) || 1;
      const half = Math.min(Math.max(probability * 180, 6), 174) + 8;
      const t0 = performance.now();
      let lastNotch = 0;
      let overArc = false;
      const tick = (): void => {
        const p = Math.min(1, (performance.now() - t0) / spinMs);
        const travelled = dist * ease(p);
        const notch = Math.floor(travelled / TICK_DEG);
        if (notch > lastNotch) { playSfx('click_wheel', 0.4); lastNotch = notch; }
        const angle = ((((startRot + dir * travelled) % 360) + 540) % 360) - 180;
        const near = Math.abs(angle) <= half;
        if (near !== overArc && arcRef.current) {
          overArc = near;
          arcRef.current.style.filter = near ? `drop-shadow(0 0 12px ${color}) drop-shadow(0 0 4px ${color})` : `drop-shadow(0 0 6px ${color}88)`;
        }
        tickRafRef.current = p < 1 ? requestAnimationFrame(tick) : null;
      };
      tickRafRef.current = requestAnimationFrame(tick);

      const id = setTimeout(() => {
        setResolved(true);
      }, spinMs);
      return () => clearTimeout(id);
    }
  }, [isSpinning, spinResult, probability, spinMs, animationsEnabled, color]);

  const failed = resolved && spinResult === 'lose';
  const won = resolved && spinResult === 'win';
  const winMs = animationsEnabled ? DIAL_WIN_MS : DIAL_FAST_WIN_MS;

  const ticks = Array.from({ length: TICK_COUNT }, (_, i) => {
    const angle = (i / TICK_COUNT) * 360;
    return { angle, dim: angle > 150 && angle < 210 };
  });

  return (
    <div
      className="relative grid place-items-center rounded-full"
      style={{ width: size, height: size }}
    >
      <svg viewBox={`0 0 ${VB} ${VB}`} width={size} height={size} className="absolute inset-0">
        <defs>
          <radialGradient id="dialFace" cx="50%" cy="42%" r="65%">
            <stop offset="0%" stopColor="#1a1c24" />
            <stop offset="100%" stopColor="#0a0b10" />
          </radialGradient>
          <linearGradient id="dialArc" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0%" stopColor="#1FA64D" />
            <stop offset="100%" stopColor="#7CFF6B" />
          </linearGradient>
        </defs>

        <circle cx={C} cy={C} r={ARC_R - 6} fill="url(#dialFace)" />


        <path d="M110 64 L152 112 L129 112 L129 156 L91 156 L91 112 L68 112 Z" fill="#ffffff" opacity="0.05" />

        {ticks.map((t, i) => {
          const rad = ((t.angle - 90) * Math.PI) / 180;
          return (
            <line
              key={i}
              x1={C + TICK_R_INNER * Math.cos(rad)} y1={C + TICK_R_INNER * Math.sin(rad)}
              x2={C + TICK_R_OUTER * Math.cos(rad)} y2={C + TICK_R_OUTER * Math.sin(rad)}
              stroke="#ffffff" strokeWidth={1.5} strokeLinecap="round" opacity={t.dim ? 0.06 : 0.18}
            />
          );
        })}

        <circle cx={C} cy={C} r={ARC_R} fill="none" stroke="#ffffff" strokeOpacity="0.06" strokeWidth="6" />

        {isKnown && (
          <circle
            ref={arcRef}
            cx={C} cy={C} r={ARC_R} fill="none" stroke={won ? WIN_COLOR : color} strokeWidth="6" strokeLinecap="round"
            strokeDasharray={`${arcLen} ${ARC_C - arcLen}`}
            transform={`rotate(${arcRotateDeg} ${C} ${C})`}
            style={{
              filter: won
                ? `drop-shadow(0 0 14px ${WIN_COLOR})`
                : `drop-shadow(0 0 10px ${color}) drop-shadow(0 0 4px ${color}aa)`,
            }}
          />
        )}

        <text x={C} y="24" textAnchor="middle" fill="#ffffff" opacity="0.85" fontSize="9" fontWeight="600">100%</text>
        <text x="16" y={C + 3} textAnchor="middle" fill="#ffffff" opacity="0.85" fontSize="9" fontWeight="600">50%</text>
        <text x={VB - 16} y={C + 3} textAnchor="middle" fill="#ffffff" opacity="0.85" fontSize="9" fontWeight="600">50%</text>
      </svg>


      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          transform: `rotate(${rotation}deg)`,
          transition: isSpinning ? `transform ${spinMs}ms ${spinEase}` : 'none',
        }}
      >
        <div
          className="absolute left-1/2 -translate-x-1/2 w-0 h-0 border-l-[7px] border-l-transparent border-r-[7px] border-r-transparent border-b-[12px] border-b-[#FF9F0A] drop-shadow-[0_0_6px_rgba(255,150,0,0.6)]"
          style={{ bottom: size * 0.04, filter: won ? `drop-shadow(0 0 10px ${WIN_COLOR})` : undefined }}
        />
      </div>


      {won && (
        <div
          aria-hidden
          className="upgrade-win-ring absolute inset-2 rounded-full border-[5px]"
          style={{
            animationDuration: `${winMs}ms`,
            borderColor: WIN_COLOR,
            boxShadow: `0 0 30px ${WIN_COLOR}b3, inset 0 0 24px ${WIN_COLOR}73`,
          }}
        />
      )}

      {failed ? (
        <>

          <div
            aria-hidden
            className="absolute inset-2 rounded-full border-[5px] border-accent-red [animation:failFlash_1.3s_ease-in-out]"
            style={{ animationDuration: `${flashMs}ms`, boxShadow: '0 0 30px rgba(255,59,48,0.7), inset 0 0 24px rgba(255,59,48,0.45)' }}
          />
          <span
            className="relative z-10 flex w-full flex-col items-center justify-center leading-none [animation:failFlash_1.3s_ease-in-out]"
            style={{ animationDuration: `${flashMs}ms` }}
          >
            {t('failedTitle').split(' ').map((word, i) => (
              <span
                key={i}
                className="font-extrabold uppercase text-accent-red text-center"
                style={{ fontSize: size * 0.12, letterSpacing: '0.5px', textShadow: '0 0 14px rgba(255,59,48,0.7)' }}
              >
                {word}
              </span>
            ))}
          </span>
        </>
      ) : (
        <span className="relative z-10 flex w-full flex-col items-center justify-center leading-none">
          <span
            ref={pctRef}
            className={won ? 'upgrade-win-pop font-bold tabular-nums' : 'font-bold tabular-nums'}
            style={{ color: won ? WIN_COLOR : color, fontSize: size * 0.18 }}
          >
            {pct !== null ? `${pct.toFixed(2)}%` : '—'}
          </span>
          {onHintRetry && probability === null ? (
            <button
              type="button"
              data-testid="upgrade-dial-retry"
              onClick={onHintRetry}
              className="flex flex-col items-center gap-1.5 transition active:opacity-70"
              style={{ marginTop: size * 0.04 }}
            >
              <span
                className="grid place-items-center rounded-full bg-error/15 text-error"
                style={{ width: size * 0.19, height: size * 0.19 }}
              >
                <FaExclamationTriangle style={{ fontSize: size * 0.1 }} />
              </span>
              <span
                className="font-semibold uppercase tracking-wider text-white/70"
                style={{ fontSize: size * 0.055 }}
              >
                {t('slotRetry')}
              </span>
            </button>
          ) : (
            <span
              ref={labelRef}
              data-testid="upgrade-dial-label"
              dir={isKnown ? 'ltr' : undefined}
              className={
                isKnown
                  ? 'font-semibold tabular-nums text-white/80 text-center leading-none'
                  : 'max-w-[54%] text-balance font-medium uppercase tracking-wider text-white/45 text-center leading-tight'
              }
              style={{ fontSize: size * (isKnown ? 0.09 : 0.06), marginTop: size * 0.05 }}
            >
              {label}
            </span>
          )}
        </span>
      )}
    </div>
  );
};
